import { useState, useEffect, useCallback } from 'react';
import { Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { accessControlAction } from '@/lib/api';

interface BankIdVerificationProps {
  deviceId: string;
  onVerified: (token: string) => void;
  onCancel: () => void;
}

type Status = 'idle' | 'initiating' | 'pending' | 'complete' | 'failed';

export function BankIdVerification({ deviceId, onVerified, onCancel }: BankIdVerificationProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [orderRef, setOrderRef] = useState<string | null>(null);
  const [autoStartToken, setAutoStartToken] = useState<string | null>(null);
  const [hintCode, setHintCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const initiate = useCallback(async () => {
    setStatus('initiating');
    setError(null);

    const result = await accessControlAction<{
      order_ref: string;
      auto_start_token: string;
      qr_start_token: string;
    }>('initiate-verification', {
      device_id: deviceId,
      method: 'bankid',
    });

    if (result.error || !result.data) {
      setError(result.error || 'Kunde inte starta BankID');
      setStatus('failed');
      return;
    }

    setOrderRef(result.data.order_ref);
    setAutoStartToken(result.data.auto_start_token);
    setStatus('pending');
  }, [deviceId]);

  // Poll for BankID completion
  useEffect(() => {
    if (status !== 'pending' || !orderRef) return;

    const interval = setInterval(async () => {
      const result = await accessControlAction<{
        status: string;
        hint_code?: string;
        verification_token?: string;
      }>('collect-verification', {
        order_ref: orderRef,
        method: 'bankid',
      });

      if (result.error) {
        setError(result.error);
        setStatus('failed');
        return;
      }

      if (result.data?.status === 'complete' && result.data.verification_token) {
        setStatus('complete');
        onVerified(result.data.verification_token);
        return;
      }

      if (result.data?.status === 'failed') {
        setError('BankID-verifiering misslyckades');
        setStatus('failed');
        return;
      }

      if (result.data?.hint_code) {
        setHintCode(result.data.hint_code);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [status, orderRef, onVerified]);

  const handleCancel = async () => {
    if (orderRef) {
      await accessControlAction('cancel-verification', { order_ref: orderRef }).catch(() => {});
    }
    onCancel();
  };

  const bankIdUrl = autoStartToken
    ? `bankid:///?autostarttoken=${autoStartToken}&redirect=null`
    : null;

  const hintMessage = getHintMessage(hintCode);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">BankID-verifiering</h3>
      <p className="text-sm text-muted-foreground">
        Verifiera din identitet med BankID för att få åtkomst.
      </p>

      {status === 'idle' && (
        <Button onClick={initiate} className="w-full">
          Starta BankID-verifiering
        </Button>
      )}

      {status === 'initiating' && (
        <div className="flex items-center justify-center gap-2 py-4">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Startar BankID...</span>
        </div>
      )}

      {status === 'pending' && (
        <div className="space-y-3">
          {bankIdUrl && (
            <Button asChild className="w-full">
              <a href={bankIdUrl}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Öppna BankID
              </a>
            </Button>
          )}
          <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{hintMessage}</span>
          </div>
        </div>
      )}

      {status === 'failed' && (
        <div className="space-y-3">
          <p className="text-sm text-destructive">{error}</p>
          <Button onClick={initiate} variant="outline" className="w-full">
            Försök igen
          </Button>
        </div>
      )}

      {status !== 'complete' && (
        <Button variant="ghost" onClick={handleCancel} className="w-full">
          Avbryt
        </Button>
      )}
    </div>
  );
}

function getHintMessage(hintCode: string | null): string {
  switch (hintCode) {
    case 'outstandingTransaction':
      return 'Starta BankID-appen...';
    case 'noClient':
      return 'Starta BankID-appen på din enhet...';
    case 'started':
      return 'Söker efter BankID...';
    case 'userSign':
      return 'Skriv in din säkerhetskod i BankID-appen...';
    default:
      return 'Väntar på BankID...';
  }
}
